<template>
  <b-modal
    v-model="showModal"
    :title="isEditMode ? 'Edit Shortage Item' : 'Add Shortage Item'"
    size="md"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form @submit.prevent="submitHandler">
      <b-row>
        <!-- Error Alert -->
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>{{ errorMessage }}</p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>{{ errorMessage.detail }}</p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>{{ errorMessage.message }}</p>
            </div>
          </b-alert>
        </b-col>

        <!-- Product Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Product
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedProduct"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="filterProductOptions"
              :class="{ 'error-border': validationErrors.product }"
              label="display_text"
              placeholder="Search product..."
              @search="onProductSearch"
              @input="handleProductChange"
            />
            <p
              v-if="validationErrors.product"
              class="error-message"
            >
              {{ validationErrors.product }}
            </p>
            <p
              v-if="errorMessage && errorMessage.code"
              class="error-message"
            >
              {{ errorMessage.code[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Quantity -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Quantity
            </template>
            <b-form-input
              v-model="minimumOrderQuantity"
              type="number"
              min="0"
              max="99999"
              placeholder="Enter quantity (max 5 digits, default: 1)"
              :class="{ 'error-border': validationErrors.minimumOrderQuantity }"
              @input="handleMinQtyChange"
            />
            <p
              v-if="validationErrors.minimumOrderQuantity"
              class="error-message"
            >
              {{ validationErrors.minimumOrderQuantity }}
            </p>
            <p
              v-if="errorMessage && errorMessage.quantity"
              class="error-message"
            >
              {{ errorMessage.quantity[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Buyers Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Buyer
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedBuyers"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="buyerOptions"
              :loading="buyerLoading"
              :class="{ 'error-border': validationErrors.buyers }"
              label="text"
              placeholder="Select Buyers"
              :reduce="data => data.id"
              :deselect-from-dropdown="true"
              @input="handleBuyersChange"
            />
            <p
              v-if="validationErrors.buyers"
              class="error-message"
            >
              {{ validationErrors.buyers }}
            </p>
            <p
              v-if="errorMessage && errorMessage.buyer"
              class="error-message"
            >
              {{ errorMessage.buyer[0] }}
            </p>
          </b-form-group>
        </b-col>

        <!-- Created By Selection -->
        <b-col cols="12">
          <b-form-group>
            <template #label>
              Salesman
              <span class="text-danger">*</span>
            </template>
            <custom-v-select
              v-model="selectedCreatedBy"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="usersList"
              :class="{ 'error-border': validationErrors.createdBy }"
              label="full_name"
              placeholder="Select Created By"
              :reduce="(data => data.id)"
              :deselect-from-dropdown="true"
              @input="handleCreatedByChange"
            />
            <p
              v-if="validationErrors.createdBy"
              class="error-message"
            >
              {{ validationErrors.createdBy }}
            </p>
            <p
              v-if="errorMessage && errorMessage.created_by"
              class="error-message"
            >
              {{ errorMessage.created_by[0] }}
            </p>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving || loading"
        @click="ok()"
      >
        {{ isEditMode ? 'Update' : 'Create' }}
        <b-spinner
          v-if="saving || loading"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput,
  BForm, BSpinner, BAlert,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
  },

  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      showModal: true,
      loading: false,
      selectedProduct: null,
      productOptions: [],
      filterProductOptions: [],
      productLoading: false,
      minimumOrderQuantity: '',
      selectedBuyers: [],
      buyerOptions: [],
      buyerLoading: false,
      selectedCreatedBy: null,
      usersList: [],
      salesmanLoading: false,
      saving: false,
      errorMessage: {},
      validationErrors: {},
      pendingProductCode: null,
      searchTimeout: null,
    }
  },

  computed: {
    isEditMode() {
      return !!(this.id)
    },
  },

  async created() {
    await this.fetchOptions()

    if (this.isEditMode) {
      await this.fetchDetails()
    }
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  },

  methods: {
    onProductSearch(search) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.filterProducts(search)
      }, 300)
    },

    filterProducts(search) {
      if (!search || search.trim() === '') {
        // Show only first 15 items when no search
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } else {
        // Filter products by search term
        const searchLower = search.toLowerCase()
        const filtered = this.productOptions.filter(product => product.name.toLowerCase().includes(searchLower)
          || product.code.toLowerCase().includes(searchLower))

        // Limit to first 50 results for performance
        this.filterProductOptions = filtered.slice(0, 50)
      }
    },

    async fetchOptions() {
      try {
        this.buyerLoading = true
        this.salesmanLoading = true

        const { data: buyersRes } = await axios.get('/accounts/buyers_options')
        this.buyerOptions = buyersRes.data

        const { data: usersList } = await axios.get('/accounts/salesmen_options_with_user_id')
        this.usersList = usersList.data

        const productRes = await axios.get('/marg/product_options')
        this.productOptions = productRes.data.data.map(product => ({
          id: product.code,
          code: product.code,
          name: product.name,
          display_text: `${product.name}`,
        }))

        // Initialize with first 15 products
        this.filterProductOptions = this.productOptions.slice(0, 15)
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || 'Failed to fetch options!'
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load options',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.buyerLoading = false
        this.salesmanLoading = false
      }
    },

    fetchDetails() {
      this.loading = true
      axios.get(`/product_shortage/${this.id}/`)
        .then(res => {
          const { data } = res

          this.minimumOrderQuantity = data.quantity || ''
          this.minimumOrderQuantityApplicable = data.minimum_order_quantity_applicable !== undefined
            ? data.minimum_order_quantity_applicable : true
          this.selectedBuyers = data.buyer || []
          this.selectedCreatedBy = data.created_by || null
          if (data.code) {
            const foundProduct = this.productOptions.find(p => p.code === data.code)
            if (foundProduct) {
              this.selectedProduct = foundProduct
              // Ensure the selected product is in the filtered options
              if (!this.filterProductOptions.find(p => p.code === data.code)) {
                this.filterProductOptions = [foundProduct, ...this.filterProductOptions.slice(0, 14)]
              }
            } else {
              this.selectedProduct = null
            }
          } else {
            this.selectedProduct = null
          }
          this.loading = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to fetch details'
          this.loading = false
        })
    },

    handleProductChange() {
      this.clearValidationError('product')
    },

    handleMinQtyChange() {
      this.clearValidationError('minimumOrderQuantity')
    },

    handleBuyersChange() {
      this.clearValidationError('buyers')
    },

    handleCreatedByChange() {
      this.clearValidationError('createdBy')
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field)
      }
    },

    validateForm() {
      this.validationErrors = {}
      let isValid = true

      // Validate product
      if (!this.selectedProduct) {
        this.validationErrors.product = 'Product is required'
        isValid = false
      }

      // Validate quantity (optional, but if provided must be valid)
      const minQty = this.minimumOrderQuantity !== null && this.minimumOrderQuantity !== undefined
        ? this.minimumOrderQuantity.toString().trim()
        : ''

      if (minQty !== '' && !/^\d{1,5}$/.test(minQty)) {
        this.validationErrors.minimumOrderQuantity = 'Must be an integer with maximum 5 digits'
        isValid = false
      }

      // Validate buyers
      if (!this.selectedBuyers || this.selectedBuyers.length === 0) {
        this.validationErrors.buyers = 'At least one buyer is required'
        isValid = false
      }

      // Validate created by
      if (!this.selectedCreatedBy) {
        this.validationErrors.createdBy = 'Created by is required'
        isValid = false
      }

      return isValid
    },

    submitHandler(event) {
      event.preventDefault()
      this.errorMessage = {}

      if (!this.validateForm()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            text: 'Please fix the errors and try again',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      // Convert quantity to integer, if 0, empty, or not provided, set to 1
      let quantity = 1 // Default value
      if (this.minimumOrderQuantity !== null && this.minimumOrderQuantity !== undefined && this.minimumOrderQuantity !== '') {
        const parsedQuantity = parseInt(this.minimumOrderQuantity, 10)
        if (!Number.isNaN(parsedQuantity) && parsedQuantity > 0) {
          quantity = parsedQuantity
        }
      }

      const payload = {
        code: this.selectedProduct.code,
        quantity,
        buyer: this.selectedBuyers,
        created_by: this.selectedCreatedBy,
      }

      this.saving = true

      const request = this.isEditMode
        ? axios.patch(`/product_shortage/${this.id}/`, payload)
        : axios.post('/product_shortage/', payload)

      request
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || (this.isEditMode ? 'Updated Successfully' : 'Created Successfully'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          this.$emit(this.isEditMode ? 'shortage-items-updated' : 'shortage-items-added')
          this.saving = false
          this.showModal = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.error || error?.response?.data || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.text-danger {
  color: #dc3545 !important;
}

input::placeholder {
  color: #6c757d !important;
  opacity: 1;
}

.vs__dropdown-toggle .vs__selected-options .vs__search::placeholder {
  color: #6c757d !important;
}
</style>
